import type {
	ConversationChannel,
	ConversationChannelTask,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	type ConversationTask,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { mapApiTaskStatusToProgressState } from './mapApiTaskStatusToProgressState';

/**
 * Maps a single API task to the local domain ConversationTask, populating all
 * camelCase fields from the snake_case network object.
 */
const mapApiTaskToDomain = (apiTask: ConversationChannelTask): ConversationTask => ({
	id: apiTask.task_id,
	taskId: apiTask.task_id,
	progressState: mapApiTaskStatusToProgressState(apiTask.status),
	title: apiTask.title,
	messageId: apiTask.message_id,
	completionMessageId: apiTask.completion_message_id,
	updatedAt: apiTask.updated_at,
	conversationId: apiTask.conversation_id,
	agentId: apiTask.agent_id,
	triggerType: apiTask.trigger_type,
	plan: apiTask.plan,
	resultSummary: apiTask.result_summary,
	errorMessage: apiTask.error_message,
	createdAt: apiTask.created_at,
	startedAt: apiTask.started_at,
	completedAt: apiTask.completed_at,
});

/**
 * Merges remote tasks with local tasks.
 *
 * - Remote tasks are sorted by `created_at` ascending (oldest first, latest last).
 * - All tasks except the last one are fully overwritten from the network.
 * - For the last task: if its `task_id` matches the last local task's `id`,
 *   merge them — local `progressState` and local-only attributes (`seekingCompletedAck`, `id`)
 *   take precedence; everything else comes from the network.
 * - If the last network task does NOT match the last local task's `id`,
 *   overwrite from network and append the last local task on top.
 */
const mergeTasks = (
	remoteTasks: ConversationChannelTask[] | undefined,
	localTasks: ConversationTask[] | undefined,
): ConversationTask[] | undefined => {
	if (!remoteTasks || remoteTasks.length === 0) {
		return localTasks;
	}

	// Sort remote tasks by created_at ascending (oldest first)
	const sorted = [...remoteTasks].sort(
		(a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
	);

	const lastLocal =
		localTasks && localTasks.length > 0 ? localTasks[localTasks.length - 1] : undefined;
	const lastRemote = sorted[sorted.length - 1];

	// Map all remote tasks except the last one directly from network
	const result: ConversationTask[] = sorted.slice(0, -1).map(mapApiTaskToDomain);

	// Handle the last remote task — merge with last local if ids match
	if (lastLocal && lastRemote.task_id === lastLocal.id) {
		result.push({
			...mapApiTaskToDomain(lastRemote),
			// Preserve local-only attributes
			id: lastLocal.id,
			seekingCompletedAck: lastLocal.seekingCompletedAck,
			todoSnapshot: lastLocal.todoSnapshot,
			// Local progress takes precedence
			progressState: lastLocal.progressState,
		});
	} else {
		// No match — overwrite from network
		result.push(mapApiTaskToDomain(lastRemote));

		// Append last local task on top if it exists
		if (lastLocal) {
			result.push(lastLocal);
		}
	}

	return result;
};

/**
 * Merges conversation data from local state and API response with network priority.
 * Prioritizes fields from the API (channelsFromAPI) as they represent the latest server state.
 * Falls back to local state (localConversation) for fields missing in the API response.
 */
export const mergeConversationWithNetworkPriority = (
	remoteConversation: ConversationChannel,
	localConversation?: Conversation,
): Conversation => {
	return {
		id: remoteConversation.id,

		// Prioritize API data if present, otherwise use local or generate new
		name: remoteConversation.name ?? localConversation?.name ?? generateConversationName(),
		timeCreated: remoteConversation.time_created ?? localConversation?.timeCreated,
		lastMessage: remoteConversation.last_message ?? localConversation?.lastMessage ?? undefined,
		lastMessageType:
			remoteConversation.last_message_type ?? localConversation?.lastMessageType ?? [],
		lastMessageTimestamp:
			remoteConversation.last_message_timestamp ?? localConversation?.lastMessageTimestamp,
		lastAgentMessageStatus:
			remoteConversation.last_agent_message_status ?? localConversation?.lastAgentMessageStatus,
		readStatus: remoteConversation.readStatus ?? localConversation?.readStatus,
		tags: remoteConversation.tags ?? localConversation?.tags,

		// Use local state data if it exists (messages, status are maintained locally)
		messages: localConversation?.messages ?? [],
		status: localConversation?.status ?? CHAT_STORE_STATUS.idle,
		traces: localConversation?.traces ?? [],

		...(localConversation?.sourceAgentId ? { sourceAgentId: localConversation.sourceAgentId } : {}),
		...(localConversation?.sourceId ? { sourceId: localConversation.sourceId } : {}),
		// Preserve runtime abort controller so Stop button works after channels rehydration (e.g. refocus)
		...(localConversation?.storeAbortController != null
			? { storeAbortController: localConversation.storeAbortController }
			: {}),
		...(localConversation?.rovoSpaceId ? { rovoSpaceId: localConversation.rovoSpaceId } : {}),
		agent: remoteConversation.agent,
		scopes: remoteConversation.scopes ?? localConversation?.scopes,
		tasks: mergeTasks(remoteConversation.tasks, localConversation?.tasks),
	};
};
